import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  MessagesSquare,
  HelpCircle,
  User as UserIcon,
  Settings as SettingsIcon,
  LogOut,
  Bell,
  LibraryBig,
  Palette,
  MessageCircleQuestion,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCrisp } from "@/hooks/useCrisp";
import { useAuth } from "@/components/AuthProvider";
import { useCabinetMemberMe } from "@/hooks/useCabinetMemberMe";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAnnouncementCenter } from "@/features/product-announcements/useAnnouncementCenter";
import { AnnouncementCenterModal } from "@/features/product-announcements/AnnouncementCenterModal";
import { NotificationBadge } from "@/features/product-announcements/NotificationBadge";

/**
 * Barre de navigation basse (fenêtre < md), miroir d'`AppSidebar`.
 *
 * L'onglet de l'agent s'appelle « Conversations » (triage 2026-10-19, choix
 * produit 2026-10-22) : sous « Nouvelle », des avocates ne le reconnaissaient
 * pas et posaient leurs questions juridiques au chat du support. Même point
 * d'entrée que « Nouvelle conversation » de la sidebar (→ /assistant), d'où la
 * même ancre de parcours.
 *
 * Le menu « Profil » porte tout ce que la sidebar offre et que la barre n'a pas
 * la place d'afficher : Modèles et Personnalisation (introuvables sur téléphone
 * avant le 2026-10-22), Aide, et « Contacter le support », seul accès au chat
 * Crisp dont la bulle est masquée en fenêtre étroite (`hooks/useCrisp.ts`).
 */

const linkItems = [
  { href: "/", icon: Home, label: "Dossiers", tour: undefined },
  { href: "/assistant", icon: MessagesSquare, label: "Conversations", tour: "new-conversation" },
];

const tabBase =
  "relative flex flex-col items-center justify-center flex-1 gap-0.5 transition-colors focus:outline-none";

const MobileNavigation = () => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { data: memberMe } = useCabinetMemberMe();
  const { openChat } = useCrisp();

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  // Identité utilisateur — mêmes règles que l'AvatarMenu de la sidebar.
  const fullName =
    memberMe?.full_name ||
    (user?.user_metadata?.full_name as string | undefined) ||
    user?.email ||
    "Utilisateur";
  const initial = (fullName.trim().charAt(0) || "?").toUpperCase();
  const avatarUrl = user?.user_metadata?.avatar_url as string | undefined;

  const center = useAnnouncementCenter();
  const [notifOpen, setNotifOpen] = useState(false);
  const unread = center?.unreadCount ?? 0;

  // Les pages rangées dans le menu Profil allument son onglet : sinon aucun
  // onglet n'est actif et l'avocat ne sait plus où il se trouve.
  const profileActive = ["/settings", "/modeles", "/personnalisation", "/aide"].some(
    (href) => location.pathname === href || location.pathname.startsWith(`${href}/`),
  );

  return (
    <>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#fafafa] border-t border-grey-200 safe-area-bottom">
        <div className="flex items-stretch justify-around h-14">
          {linkItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);

            return (
              <Link
                key={item.href}
                to={item.href}
                data-tour={item.tour}
                className={cn(
                  tabBase,
                  active ? "text-[#1B1B1E]" : "text-grey-400 active:text-grey-600",
                )}
              >
                <Icon className={cn("h-5 w-5", active && "stroke-[2.5]")} />
                <span className="text-xs font-medium leading-tight">{item.label}</span>
                {active && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#1B1B1E] rounded-b-full" />
                )}
              </Link>
            );
          })}

          {/* Onglet Profil — déclenche le menu (s'ouvre vers le haut). Aligné `end`
              car c'est l'onglet le plus à droite : évite tout débordement à droite. */}
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                tabBase,
                profileActive ? "text-[#1B1B1E]" : "text-grey-400 active:text-grey-600",
              )}
              aria-label={
                unread > 0
                  ? `Ouvrir le menu utilisateur, ${unread} ${unread > 1 ? "notifications non lues" : "notification non lue"}`
                  : "Ouvrir le menu utilisateur"
              }
            >
              <div className="relative">
                <Avatar className="h-5 w-5 border border-black/5">
                  {avatarUrl ? <AvatarImage src={avatarUrl} alt={fullName} /> : null}
                  <AvatarFallback className="bg-[#2E2F33] text-white text-[10px] font-medium">
                    {initial}
                  </AvatarFallback>
                </Avatar>
                <NotificationBadge count={unread} className="absolute -top-1 -right-1 ring-2 ring-[#fafafa]" />
              </div>
              <span className="text-xs font-medium leading-tight">Profil</span>
              {profileActive && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#1B1B1E] rounded-b-full" />
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="end" sideOffset={8} className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/settings#profile">
                  <UserIcon className="mr-2 h-4 w-4" />
                  Mon profil
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings">
                  <SettingsIcon className="mr-2 h-4 w-4" />
                  Paramètres
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/modeles">
                  <LibraryBig className="mr-2 h-4 w-4" />
                  Bibliothèque de modèles
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/personnalisation">
                  <Palette className="mr-2 h-4 w-4" />
                  Personnalisation
                </Link>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {center && center.items.length > 0 && (
                <DropdownMenuItem
                  onSelect={() => setNotifOpen(true)}
                  aria-label={
                    unread > 0
                      ? `Notifications, ${unread} non ${unread > 1 ? "lues" : "lue"}`
                      : "Notifications"
                  }
                >
                  <Bell className="mr-2 h-4 w-4" />
                  <span className="flex-1">Notifications</span>
                  <NotificationBadge count={unread} />
                </DropdownMenuItem>
              )}

              <DropdownMenuItem asChild>
                <Link to="/aide">
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Aide
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => openChat()}>
                <MessageCircleQuestion className="mr-2 h-4 w-4" />
                Contacter le support
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                className="text-red-600 focus:text-red-700 focus:bg-red-50"
                onSelect={() => signOut()}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      {/* Modal des annonces — rendue HORS du DropdownMenuContent (sibling du menu) :
          sinon Radix la démonte à la fermeture du menu et crée une course au focus. */}
      {center && center.items.length > 0 && (
        <AnnouncementCenterModal
          announcements={center.items}
          readIds={center.readIds}
          open={notifOpen}
          onClose={() => setNotifOpen(false)}
          onMarkRead={center.markRead}
          onMarkAllRead={center.markAllRead}
        />
      )}
    </>
  );
};

export default MobileNavigation;
